import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  Alert,
  Stack,
  AppBar,
  Toolbar
} from '@mui/material';
import MapIcon from '@mui/icons-material/MyLocation';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  Polyline,
  useMap
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { parseLiveTrackShare } from '../../utils/liveTrackUrl';
import { apiUrl } from '../../utils/apiUrl';

function geoCodeName(code) {
  if (code === 1) return 'PERMISSION_DENIED';
  if (code === 2) return 'POSITION_UNAVAILABLE';
  if (code === 3) return 'TIMEOUT';
  if (code === -1) return 'NOT_SUPPORTED';
  if (code === -2) return 'INSECURE_CONTEXT';
  return 'UNKNOWN';
}

/** @param {number} code */
function geoCodeHints(code) {
  if (code === 1) {
    return [
      'Allow location for this site in the browser address bar or Site settings.',
      'Safari: Settings → Websites → Location. Chrome: lock icon → Site settings → Location.'
    ];
  }
  if (code === 2) {
    return [
      'Very common on Mac/desktop: there is no GPS chip; “high accuracy” can fail while Wi‑Fi–based location still works.',
      'If the console shows CoreLocation / kCLErrorLocationUnknown, macOS could not fix your position — turn on Wi‑Fi (helps coarse location), quit VPN if applicable, and System Settings → Privacy & Security → Location Services → enable your browser.',
      'Try the buttons below for approximate (Wi‑Fi) vs precise, or use “Enter coordinates instead”.',
      'Testing on a phone with GPS often succeeds immediately.'
    ];
  }
  if (code === 3) {
    return ['Move outdoors or retry; you can increase timeout or use approximate location.'];
  }
  if (code === -2) {
    return ['Open the app over HTTPS or http://localhost — not a plain HTTP LAN IP.'];
  }
  return [];
}

/** Advance Garmin `begin` cursor by 1 ms so the next poll does not repeat the last point. */
function bumpBeginIso(iso) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms + 1).toISOString();
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function FitBoth({ athlete, viewer }) {
  const map = useMap();
  useEffect(() => {
    const pts = [];
    if (athlete) pts.push([athlete.lat, athlete.lon]);
    if (viewer) pts.push([viewer.lat, viewer.lon]);
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView(pts[0], 14);
      return;
    }
    map.fitBounds(pts, { padding: [56, 56], maxZoom: 16 });
  }, [athlete, viewer, map]);
  return null;
}

function MapResize({ active }) {
  const map = useMap();
  useEffect(() => {
    if (!active) return undefined;
    const t = requestAnimationFrame(() => {
      map.invalidateSize();
    });
    return () => cancelAnimationFrame(t);
  }, [active, map]);
  return null;
}

/**
 * LiveTrack companion: paste a Garmin share link, see athlete + your position on one map.
 */
const TrackPage = () => {
  const [urlInput, setUrlInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [token, setToken] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [pollMs, setPollMs] = useState(15000);
  const [sessionName, setSessionName] = useState('');
  const [athleteName, setAthleteName] = useState('');
  const [athletePos, setAthletePos] = useState(null);
  const [trail, setTrail] = useState([]);
  const [viewerPos, setViewerPos] = useState(null);
  /** When GPS is unreliable (common on desktop), crew can pin lat/lon */
  const [viewerManualMode, setViewerManualMode] = useState(false);
  const [manualLatInput, setManualLatInput] = useState('');
  const [manualLonInput, setManualLonInput] = useState('');
  const [manualViewerPos, setManualViewerPos] = useState(null);
  const [manualApplyError, setManualApplyError] = useState(null);
  const [geoIssue, setGeoIssue] = useState(null);
  /** Desktop Wi‑Fi fix often needs false; GPS phones can use true */
  const [geoHighAccuracy, setGeoHighAccuracy] = useState(false);
  const [geoRetryNonce, setGeoRetryNonce] = useState(0);
  const [fetchError, setFetchError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [athleteStalePolls, setAthleteStalePolls] = useState(0);
  /** Avoid loading Garmin’s SPA in an iframe by default — it calls their API from the browser and often logs 403s; our map uses `/api/livetrack/sync` instead. */
  const [showGarminEmbed, setShowGarminEmbed] = useState(false);
  const watchIdRef = useRef(null);
  const pollRef = useRef(null);
  /** ISO timestamp for GET .../track-points/common?begin= */
  const trackBeginCursorRef = useRef(null);
  const seenTrackDateTimesRef = useRef(new Set());

  const defaultCenter = useMemo(() => [39.8, -98.5], []);

  const garminEmbedUrl = useMemo(() => {
    if (!tracking || !sessionId || !token) return null;
    return `https://livetrack.garmin.com/session/${sessionId}/token/${token}`;
  }, [tracking, sessionId, token]);

  const effectiveViewerPos = manualViewerPos ?? viewerPos;

  const applySessionPayload = useCallback((data) => {
    if (typeof data.postTrackPointFrequency === 'number' && data.postTrackPointFrequency > 0) {
      setPollMs(Math.min(60000, Math.max(5000, data.postTrackPointFrequency * 1000)));
    }
    if (data.sessionName) setSessionName(data.sessionName);
    if (data.userDisplayName) setAthleteName(data.userDisplayName.trim());
    setLastUpdated(new Date());
  }, []);

  const consumeTrackPointsBatch = useCallback((pts) => {
    if (!Array.isArray(pts) || pts.length === 0) {
      setAthleteStalePolls((n) => n + 1);
      return;
    }

    const seen = seenTrackDateTimesRef.current;
    const newSegments = [];
    let maxMs = -Infinity;

    for (const p of pts) {
      const dt = p.dateTime;
      const la = p.position?.lat;
      const lo = p.position?.lon;
      if (!dt || typeof la !== 'number' || typeof lo !== 'number') continue;
      if (seen.has(dt)) continue;
      seen.add(dt);
      const tms = Date.parse(dt);
      if (!Number.isNaN(tms)) maxMs = Math.max(maxMs, tms);
      newSegments.push([la, lo]);
    }

    if (newSegments.length === 0) {
      setAthleteStalePolls((n) => n + 1);
      return;
    }

    setAthleteStalePolls(0);

    setTrail((prev) => {
      const next = [...prev];
      for (const [la, lo] of newSegments) {
        const last = next[next.length - 1];
        if (last && haversineKm(last[0], last[1], la, lo) < 0.002) continue;
        next.push([la, lo]);
      }
      return next.length > 800 ? next.slice(-800) : next;
    });

    const lastPt = pts[pts.length - 1];
    if (lastPt?.position && typeof lastPt.position.lat === 'number') {
      setAthletePos({
        lat: lastPt.position.lat,
        lon: lastPt.position.lon
      });
    }

    if (maxMs > -Infinity) {
      trackBeginCursorRef.current = bumpBeginIso(new Date(maxMs).toISOString());
    }

    if (seen.size > 4000) {
      seenTrackDateTimesRef.current = new Set([...seen].slice(-2000));
    }
  }, []);

  const fetchLiveSync = useCallback(async () => {
    if (!sessionId || !token) return;
    setFetchError(null);
    try {
      const params = new URLSearchParams({
        sessionId,
        token
      });
      if (trackBeginCursorRef.current) {
        params.set('begin', trackBeginCursorRef.current);
      }
      const res = await fetch(apiUrl(`/api/livetrack/sync?${params}`));
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          body.error ||
          body.details?.error ||
          `Request failed (${res.status})`;
        throw new Error(msg);
      }
      if (body.session) applySessionPayload(body.session);

      if (
        body.trackPointsOk &&
        body.trackPoints &&
        Array.isArray(body.trackPoints.trackPoints)
      ) {
        consumeTrackPointsBatch(body.trackPoints.trackPoints);
      } else if (!body.trackPointsOk && body.trackPointsHttpStatus) {
        console.warn(
          'Garmin track-points:',
          body.trackPointsError || `HTTP ${body.trackPointsHttpStatus}`
        );
      }
    } catch (e) {
      setFetchError(e instanceof Error ? e.message : String(e));
    }
  }, [sessionId, token, applySessionPayload, consumeTrackPointsBatch]);

  useEffect(() => {
    if (!tracking || !sessionId || !token) return undefined;

    fetchLiveSync();
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(fetchLiveSync, pollMs);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [tracking, sessionId, token, pollMs, fetchLiveSync]);

  useEffect(() => {
    if (!tracking || viewerManualMode) {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (viewerManualMode) setGeoIssue(null);
      else if (!tracking) setGeoIssue(null);
      return undefined;
    }
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      setGeoIssue({
        code: -2,
        message: 'Geolocation only works in a secure context (HTTPS or localhost).'
      });
      return undefined;
    }
    if (!navigator.geolocation) {
      setGeoIssue({ code: -1, message: 'This browser does not support geolocation.' });
      return undefined;
    }

    setGeoIssue(null);

    const opts = {
      enableHighAccuracy: geoHighAccuracy,
      maximumAge: geoHighAccuracy ? 5000 : 45000,
      timeout: geoHighAccuracy ? 25000 : 40000
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        setGeoIssue(null);
        setViewerPos({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy
        });
      },
      (err) => {
        setGeoIssue({
          code: err.code,
          message: err.message || 'Location error'
        });
      },
      opts
    );
    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [tracking, geoHighAccuracy, geoRetryNonce, viewerManualMode]);

  const distanceMi =
    athletePos && effectiveViewerPos
      ? haversineKm(
          athletePos.lat,
          athletePos.lon,
          effectiveViewerPos.lat,
          effectiveViewerPos.lon
        ) * 0.621371
      : null;

  const handleStart = () => {
    const parsed = parseLiveTrackShare(urlInput);
    if (!parsed) {
      setFetchError(
        'Paste the full LiveTrack link from Garmin (must contain livetrack.garmin.com/session/…/token/…).'
      );
      return;
    }
    setSessionId(parsed.sessionId);
    setToken(parsed.token);
    setTrail([]);
    setAthletePos(null);
    setFetchError(null);
    setGeoIssue(null);
    setGeoHighAccuracy(false);
    setGeoRetryNonce(0);
    setViewerManualMode(false);
    setManualLatInput('');
    setManualLonInput('');
    setManualViewerPos(null);
    setManualApplyError(null);
    trackBeginCursorRef.current = null;
    seenTrackDateTimesRef.current = new Set();
    setAthleteStalePolls(0);
    setShowGarminEmbed(false);
    setTracking(true);
  };

  const handleStop = () => {
    setTracking(false);
    setSessionId(null);
    setToken(null);
    setGeoIssue(null);
    setViewerPos(null);
    setViewerManualMode(false);
    setManualLatInput('');
    setManualLonInput('');
    setManualViewerPos(null);
    setManualApplyError(null);
    trackBeginCursorRef.current = null;
    seenTrackDateTimesRef.current = new Set();
    setAthleteStalePolls(0);
    setShowGarminEmbed(false);
  };

  const applyManualViewerPin = () => {
    const lat = parseFloat(manualLatInput);
    const lon = parseFloat(manualLonInput);
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lon) ||
      lat < -90 ||
      lat > 90 ||
      lon < -180 ||
      lon > 180
    ) {
      setManualApplyError('Use latitude −90–90 and longitude −180–180 (decimal degrees).');
      return;
    }
    setManualApplyError(null);
    setManualViewerPos({ lat, lon });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            LiveTrack map
          </Typography>
          <Button color="inherit" href="/">
            Race plans
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
        <Paper
          sx={{
            p: 2,
            mb: 2,
            position: 'relative',
            zIndex: 2,
            isolation: 'isolate'
          }}
        >
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Paste the Garmin LiveTrack URL you were shared. The athlete path and blue marker come from
              Garmin’s <strong>track-points</strong> feed (same source their site uses to snap to current
              location). Green is you — device GPS or manual coordinates.
            </Typography>
            <TextField
              label="LiveTrack URL"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              placeholder="https://livetrack.garmin.com/session/…/token/…"
              disabled={tracking}
              size="small"
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {!tracking ? (
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStart}
                >
                  Start tracking
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                >
                  Stop
                </Button>
              )}
              {tracking && (
                <Button
                  startIcon={<MapIcon />}
                  variant="outlined"
                  onClick={() => {
                    fetchLiveSync();
                  }}
                >
                  Refresh now
                </Button>
              )}
            </Stack>
            {tracking && (
              <Stack
                spacing={1.5}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover'
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Your position (green dot)
                </Typography>
                {!viewerManualMode ? (
                  <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    fullWidth
                    size="large"
                    onClick={() => {
                      setViewerManualMode(true);
                      setManualApplyError(null);
                    }}
                  >
                    Enter my coordinates
                  </Button>
                ) : (
                  <>
                    <Button
                      type="button"
                      variant="text"
                      size="small"
                      onClick={() => {
                        setViewerManualMode(false);
                        setManualViewerPos(null);
                        setManualApplyError(null);
                      }}
                    >
                      ← Use device GPS instead
                    </Button>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="stretch">
                      <TextField
                        label="Latitude"
                        value={manualLatInput}
                        onChange={(e) => setManualLatInput(e.target.value)}
                        placeholder="e.g. 39.8712"
                        size="small"
                        fullWidth
                        inputProps={{ inputMode: 'decimal' }}
                      />
                      <TextField
                        label="Longitude"
                        value={manualLonInput}
                        onChange={(e) => setManualLonInput(e.target.value)}
                        placeholder="e.g. -79.4901"
                        size="small"
                        fullWidth
                        inputProps={{ inputMode: 'decimal' }}
                      />
                      <Button
                        type="button"
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{ minWidth: { sm: 180 }, alignSelf: { sm: 'center' } }}
                        onClick={applyManualViewerPin}
                      >
                        Place me on map
                      </Button>
                    </Stack>
                    {manualApplyError && (
                      <Typography variant="caption" color="error">
                        {manualApplyError}
                      </Typography>
                    )}
                    {manualViewerPos && (
                      <Typography variant="caption" color="text.secondary">
                        Using ({manualViewerPos.lat.toFixed(5)}, {manualViewerPos.lon.toFixed(5)}).
                      </Typography>
                    )}
                  </>
                )}
              </Stack>
            )}
            {tracking && (
              <Typography variant="caption" color="text.secondary">
                Polling about every {Math.round(pollMs / 1000)}s (from LiveTrack when available).
                {lastUpdated &&
                  ` Last athlete update: ${lastUpdated.toLocaleTimeString()}.`}
              </Typography>
            )}
            {(sessionName || athleteName) && tracking && (
              <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap>
                {sessionName && <ChipLike label={sessionName} />}
                {athleteName && <ChipLike label={athleteName} muted />}
              </Stack>
            )}
            {tracking && athleteStalePolls >= 4 && (
              <Alert severity="info">
                No new track points for ~{athleteStalePolls} polls (~
                {Math.max(1, Math.round((athleteStalePolls * pollMs) / 60000))} min). The runner may be
                paused, signal may be weak, or LiveTrack may be batching uploads.
              </Alert>
            )}
            {fetchError && (
              <Alert severity="error">
                {fetchError}
                {typeof window !== 'undefined' &&
                  window.location.hostname === 'localhost' && (
                  <Box sx={{ mt: 1, typography: 'caption' }}>
                    Local API: run{' '}
                    <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5 }}>
                      npx wrangler pages dev build --kv=SHARED_PLANS --port 8788
                    </Box>{' '}
                    then set{' '}
                    <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5 }}>
                      REACT_APP_API_URL=http://localhost:8788
                    </Box>{' '}
                    for <Box component="code">npm start</Box>.
                  </Box>
                )}
              </Alert>
            )}
            {geoIssue && tracking && !viewerManualMode && (
              <Alert severity="warning">
                <Typography variant="subtitle2" gutterBottom>
                  Your location (green dot) — not Garmin
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {geoIssue.message}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Browser code {geoIssue.code} ({geoCodeName(geoIssue.code)}) — open DevTools → Console
                  for other errors; Network tab shows only Garmin requests.
                </Typography>
                {geoCodeHints(geoIssue.code).map((hint) => (
                  <Typography key={hint} variant="caption" display="block" sx={{ mb: 0.5 }}>
                    • {hint}
                  </Typography>
                ))}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setViewerManualMode(true);
                      setManualApplyError(null);
                    }}
                  >
                    Enter coordinates instead
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setGeoRetryNonce((n) => n + 1);
                    }}
                  >
                    Retry location
                  </Button>
                  {geoIssue.code === 2 && geoHighAccuracy && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setGeoHighAccuracy(false);
                        setGeoRetryNonce((n) => n + 1);
                      }}
                    >
                      Use approximate (Wi‑Fi) location
                    </Button>
                  )}
                  {geoIssue.code === 2 && !geoHighAccuracy && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setGeoHighAccuracy(true);
                        setGeoRetryNonce((n) => n + 1);
                      }}
                    >
                      Try precise GPS instead
                    </Button>
                  )}
                </Stack>
              </Alert>
            )}
            {distanceMi != null && tracking && (
              <Typography variant="body2">
                Straight-line distance:{' '}
                <strong>{distanceMi.toFixed(2)} mi</strong> ({(distanceMi / 0.621371).toFixed(2)}{' '}
                km)
              </Typography>
            )}
            {tracking && !effectiveViewerPos && !viewerManualMode && (
              <Typography variant="caption" color="text.secondary">
                Waiting for your location… allow the prompt, or tap Enter my coordinates above.
              </Typography>
            )}
            {tracking && viewerManualMode && !manualViewerPos && (
              <Typography variant="caption" color="text.secondary">
                Enter coordinates and tap “Place me on map”.
              </Typography>
            )}
          </Stack>
        </Paper>

        <Paper
          sx={{
            overflow: 'hidden',
            borderRadius: 1,
            position: 'relative',
            zIndex: 0
          }}
        >
          <Box sx={{ height: { xs: '55vh', md: '480px' }, width: '100%' }}>
            <MapContainer
              center={athletePos ? [athletePos.lat, athletePos.lon] : defaultCenter}
              zoom={athletePos ? 14 : 4}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapResize active={tracking} />
              <FitBoth athlete={athletePos} viewer={effectiveViewerPos} />
              {trail.length > 1 && (
                <Polyline positions={trail} pathOptions={{ color: '#1565c0', weight: 4, opacity: 0.85 }} />
              )}
              {athletePos && (
                <CircleMarker
                  center={[athletePos.lat, athletePos.lon]}
                  radius={10}
                  pathOptions={{
                    color: '#0d47a1',
                    fillColor: '#1976d2',
                    fillOpacity: 1,
                    weight: 2
                  }}
                >
                  <Popup>Athlete — latest LiveTrack track point</Popup>
                </CircleMarker>
              )}
              {effectiveViewerPos && (
                <CircleMarker
                  center={[effectiveViewerPos.lat, effectiveViewerPos.lon]}
                  radius={9}
                  pathOptions={{
                    color: '#1b5e20',
                    fillColor: '#2e7d32',
                    fillOpacity: 1,
                    weight: 2
                  }}
                >
                  <Popup>
                    {manualViewerPos
                      ? `You (manual pin)`
                      : `You (this device)${viewerPos?.accuracy ? ` ±${Math.round(viewerPos.accuracy)}m` : ''}`}
                  </Popup>
                </CircleMarker>
              )}
            </MapContainer>
          </Box>
        </Paper>

        {tracking && garminEmbedUrl && (
          <Paper sx={{ mt: 2, overflow: 'hidden', borderRadius: 1 }}>
            <Box sx={{ p: 2, pb: showGarminEmbed ? 1 : 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Garmin LiveTrack (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1 }}>
                The map above uses RacePlan’s Garmin proxy — no iframe needed. Opening Garmin below loads
                their site in-page; DevTools may show <strong>403</strong> requests from{' '}
                <Box component="code" sx={{ bgcolor: 'action.hover', px: 0.5 }}>
                  livetrack.garmin.com
                </Box>{' '}
                (their session cookies don’t apply the same way here). That does not affect our blue
                athlete marker.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  href={garminEmbedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open LiveTrack in a new tab
                </Button>
                {!showGarminEmbed ? (
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowGarminEmbed(true)}
                  >
                    Show embedded Garmin viewer
                  </Button>
                ) : (
                  <Button size="small" variant="text" onClick={() => setShowGarminEmbed(false)}>
                    Hide embedded viewer
                  </Button>
                )}
              </Stack>
            </Box>
            {showGarminEmbed && (
              <Box
                sx={{
                  height: { xs: 420, md: 520 },
                  width: '100%',
                  borderTop: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.900'
                }}
              >
                <Box
                  component="iframe"
                  title="Garmin LiveTrack"
                  src={garminEmbedUrl}
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  sx={{
                    display: 'block',
                    border: 0,
                    width: '100%',
                    height: '100%',
                    minHeight: 320
                  }}
                />
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

function ChipLike({ label, muted }) {
  return (
    <Box
      component="span"
      sx={{
        typography: 'caption',
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: muted ? 'action.hover' : 'primary.dark',
        color: muted ? 'text.secondary' : 'primary.contrastText'
      }}
    >
      {label}
    </Box>
  );
}

export default TrackPage;
